import type { SVGProps } from "react"

/**
 * The Coval mark's geometry, shared by both wrappers below so the path data
 * lives in exactly one place. Stroke color and size come from the wrapper.
 */
/**
 * `strokeScale` keeps the drawn line weight constant when a caller renders the
 * artwork into a larger viewBox than the 86 it was drawn for — without it, the
 * padding that shrinks the mark thins its strokes by the same factor.
 */
const CovalMarkPaths = ({ strokeScale = 1 }: { strokeScale?: number }) => (
  <>
    <path
      strokeWidth={5.25 * strokeScale}
      d="M3.96912 21.2938C5.11437 12.2423 12.2423 5.11437 21.2938 3.96912C35.4581 2.17696 49.7919 2.17696 63.9562 3.96912C73.0077 5.11437 80.1356 12.2423 81.2809 21.2938C83.073 35.4581 83.073 49.7919 81.2809 63.9562C80.1356 73.0077 73.0077 80.1356 63.9562 81.2809C49.7919 83.073 35.4581 83.073 21.2938 81.2809C12.2423 80.1356 5.11437 73.0077 3.96912 63.9562C2.17696 49.7919 2.17696 35.4581 3.96912 21.2938Z"
    />
    <path
      strokeWidth={5.5 * strokeScale}
      d="M36.6763 22.3469C40.1011 19.6913 44.8893 19.6913 48.3142 22.3469C53.6736 26.5024 58.4879 31.3167 62.6434 36.6761C65.299 40.101 65.2989 44.8892 62.6434 48.314C58.4879 53.6734 53.6736 58.4877 48.3142 62.6432C44.8893 65.2988 40.1011 65.2988 36.6763 62.6432C31.3169 58.4877 26.5025 53.6734 22.347 48.314C19.6915 44.8892 19.6915 40.101 22.347 36.6761C26.5025 31.3167 31.3169 26.5024 36.6763 22.3469Z"
    />
    <path
      strokeWidth={5.5 * strokeScale}
      d="M42.6966 67.7434C34.0423 67.7434 21.6521 84.7845 10.8927 74.1096C0.912984 64.2082 19.3911 51.1549 19.3911 42.5685C19.3911 33.9822 0.133318 21.818 10.8927 11.1431C20.8725 1.24174 34.0423 17.4908 42.6966 17.4908C51.3509 17.4908 63.5982 0.468205 74.3576 11.1431C84.3374 21.0445 65.8593 33.9822 65.8593 42.5685C65.8593 51.1549 85.117 63.4347 74.3576 74.1096C64.3779 84.0109 51.3509 67.7434 42.6966 67.7434Z"
    />
  </>
)

interface CovalLogoProps {
  theme: string
  width?: number
  height?: number
}

/**
 * The mark on a known backdrop, where the caller states the theme and gets a
 * fixed black or white stroke (page chrome, the error boundary, the footer).
 */
const CovalLogo = ({ theme, width = 16, height = 16 }: CovalLogoProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={width}
    height={height}
    viewBox="0 0 86 86"
    fill="none"
    stroke={theme === "light" ? "#000" : "#fff"}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <CovalMarkPaths />
  </svg>
)

/**
 * The mark's own box: the artwork is drawn edge to edge inside it.
 */
const MARK_BOX = 86

/**
 * Lucide draws its glyphs inside roughly 5/6 of its 24-unit box, so an icon set
 * beside them reads correctly only if it leaves the same margin. The mark fills
 * its box, so at an identical width it looks a size larger than every icon in
 * the row. Padding the viewBox out to that ratio matches their optical size;
 * the stroke widths scale with it so the line weight is unchanged.
 */
const ICON_BOX = MARK_BOX * (6 / 5)
const ICON_ORIGIN = -(ICON_BOX - MARK_BOX) / 2

/**
 * The same mark as an ordinary icon: it inherits the surrounding text color, so
 * it sits alongside lucide icons in the sidebar nav and tracks active, hover,
 * and dark-mode states without the caller resolving a theme.
 */
export const CovalMark = (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={16}
    height={16}
    viewBox={`${ICON_ORIGIN} ${ICON_ORIGIN} ${ICON_BOX} ${ICON_BOX}`}
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <CovalMarkPaths strokeScale={ICON_BOX / MARK_BOX} />
  </svg>
)

export default CovalLogo
